import { CellContext } from '@tanstack/react-table'
import { useFormContext } from 'react-hook-form'

import { TextField } from 'components/Form'
import { Schema } from 'features/globalSetup/pages/Prebid/Bidders/EditBidderPage/pages/BidParamsAndConnection/form/schema'
import { ParamsKind } from 'features/globalSetup/pages/Prebid/Bidders/types'
import { AdUnitParameter } from 'features/globalSetup/types/adUnitBidder'
import { useAbility } from 'providers/casl'
import { useExistingWorkspaceBidder } from '../../../hooks/useExistingWorkspaceBidder'
import { AdUnitsForTable } from '../types'

type FieldPath = `adUnitBidders.${string}.${ParamsKind}Params.${string}`

type Props = {
  cellContext: CellContext<AdUnitsForTable, unknown>
  kind: ParamsKind
}

type SchemaSlice = Pick<Schema, 'adUnitBidders'>

export const AdUnitParamInputCell = ({
  cellContext: {
    cell: { getValue },
    row: {
      original: { id: adUnitBidderId },
    },
  },
  kind,
}: Props): JSX.Element | undefined => {
  const ability = useAbility()
  const { clearErrors, control, getValues, register } = useFormContext<SchemaSlice>()
  const {
    workspaceBidder: { accountType },
  } = useExistingWorkspaceBidder()

  const { name, requiredOff, value } = getValue() as AdUnitParameter

  const fieldPath: FieldPath = `adUnitBidders.${adUnitBidderId}.${kind}Params.${name}`

  if (getValues(fieldPath) === undefined) {
    register(fieldPath, { value })
  }

  const isDisabledByAuth = (): boolean => {
    switch (accountType) {
      case 'own':
        return ability.cannot('update', 'GlobalSetupField-prebid-bidders-bid-params-bid-param-own')
      case 'thirdPartyProvider':
        return ability.cannot(
          'update',
          'GlobalSetupField-prebid-bidders-bid-params-bid-param-thirdPartyProvider',
        )
      case 'yieldbird':
        return ability.cannot(
          'update',
          'GlobalSetupField-prebid-bidders-bid-params-bid-param-yielbdird',
        )
    }
  }

  return (
    <TextField
      control={control}
      disabled={isDisabledByAuth()}
      key={`${kind}-${name}`}
      name={fieldPath}
      onClearError={requiredOff ? () => void clearErrors(fieldPath) : undefined}
      useTooltipForErrors={true}
      width='sm'
    />
  )
}
